import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import {
  faFilter,
  faObjectGroup,
  faPen,
  faTrash,
  faTrashArrowUp,
} from '@fortawesome/pro-light-svg-icons';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { Actions, ofType } from '@ngrx/effects';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { TimeFormatType } from '../../../core/pipes/time-format.pipe';
import {
  NotificationType,
  ShiveNotificationService,
} from '../../../core/services/controls/shive-notification.service';
import { SharedClockHandlerService } from '../../../core/services/features/time-tracking/shared-clock-handler.service';
import { SharedTimerHandlerService } from '../../../core/services/features/time-tracking/shared-timer-handler.service';
import { SharedTrackerHandlerService } from '../../../core/services/features/time-tracking/shared-tracker-handler.service';
import { TimeBlockHttpService } from '../../../core/state/time-blocks/time-block-entries/time-block-http.service';
import * as fromTimeBlockActions from '../../../core/state/time-blocks/time-block-entries/time-blocks.actions';
import * as fromTimerActions from '../../../core/state/time-blocks/timer/timer.actions';
import { TimerStatus } from '../../../features/calendar/calendar-types';
import { CalendarService } from '../../../features/calendar/services/calendar.service';
import { ShiveGridWrapperComponent } from '../../../shared/components/shive-controls/shive-grid/shive-grid-wrapper.component';
import { TimeUnits } from '../../../shared/data-types/unit-types';
import { TimeEntriesStore } from './state-and-data-handling/time-entries-data.store';
import { TimeEntriesListViewHttpService } from './time-entries-list-view-http.service';
import { TimeEntriesListViewService } from './time-entries-list-view.service';
import { TimeBlockListEntry } from './time-enty-list-types';

@Component({
  selector: 'app-list-view',
  templateUrl: './time-entries-list-view.component.html',
  standalone: false,
  providers: [TimeEntriesStore, TimeEntriesListViewService, TimeEntriesListViewHttpService],
})
export class TimeEntriesListViewComponent implements OnInit, AfterViewInit, OnDestroy {
  // Signals
  public selectedKeys: number[] = [];
  // Viewchild
  @ViewChild('gridWrapperComponent') gridWrapperComponent: ShiveGridWrapperComponent;
  // Constants
  public readonly TimeFormatType = TimeFormatType;
  public readonly TimeUnits = TimeUnits;
  public readonly faFilter = faFilter;
  public readonly faPlay = faPlay;
  public readonly faObjectGroup = faObjectGroup;
  public readonly faTrash = faTrash;
  // Misc
  public group: GroupDescriptor[] = [];
  public aggregateMode: TimeUnits = TimeUnits.None;
  public gridData$: Observable<TimeBlockListEntry[] | null> = null;

  // DI
  public readonly timeEntriesListViewService = inject(TimeEntriesListViewService);
  public readonly timeEntriesListViewHttpService = inject(TimeEntriesListViewHttpService);
  public readonly timeBlockHttpService = inject(TimeBlockHttpService);
  private readonly calendarService = inject(CalendarService);
  private readonly sharedTrackerHandlerService = inject(SharedTrackerHandlerService);
  private readonly sharedTimerHandlerService = inject(SharedTimerHandlerService);
  private readonly sharedClockHandlerService = inject(SharedClockHandlerService);
  private readonly shiveNotificationService = inject(ShiveNotificationService);
  private readonly actions$ = inject(Actions);

  // Misc
  private readonly subs = new SubSink();

  ngOnInit(): void {
    this.loadTimeBlockEntries();
    this.initConfigData();
  }

  ngAfterViewInit(): void {
    this.initContextMenuCallbacks();
  }

  ngOnDestroy(): void {
    this.sharedTrackerHandlerService.deregisterTimeTracking();
    this.sharedTimerHandlerService.deregisterTimeTracking();
    if (this.timeEntriesListViewService.trackerSub$) {
      this.timeEntriesListViewService.trackerSub$.unsubscribe();
    }
    this.subs.unsubscribe();
  }

  public setAggregateMode(mode: TimeUnits): void {
    this.aggregateMode = mode;

    if (mode === TimeUnits.None) {
      this.group = [];
      return;
    }

    this.group = [
      {
        field: 'date',
        aggregates: [{ field: 'workingTime', aggregate: 'sum' }],
      },
    ];
  }

  private loadTimeBlockEntries() {
    this.timeEntriesListViewHttpService
      .loadTimeBlocks()
      .subscribe(() => this.timeEntriesListViewService.initActiveTimeBlockTracking());

    this.gridData$ = this.timeEntriesListViewService.timeEntriesStore.entries$;
  }

  /**
   * We don't just reload the time blocks here since the tracker time entry row would disappear and appear which leads to an ugly flickering.
   */
  private initHttpCallbacks(): void {
    this.subs.sink = this.actions$
      .pipe(
        ofType(
          fromTimeBlockActions.timeBlockAdded,
          fromTimeBlockActions.timeBlockUpdated,
          fromTimeBlockActions.timeBlockDeleted,
          fromTimeBlockActions.multipleTimeBlocksDeleted,
        ),
      )
      .subscribe((response) => {
        if (response.type === fromTimeBlockActions.timeBlockAdded.type) {
          const timeBlock = response.payload.modifiedTimeBlockItems[0];
          this.timeEntriesListViewService.addToStore(timeBlock);
        } else if (response.type === fromTimeBlockActions.timeBlockUpdated.type) {
          const original = response.payload.originalTimeBlockItem;
          const updated = response.payload.modifiedTimeBlockItems[0];
          this.timeEntriesListViewService.replaceInStore(original, updated);
        } else if (response.type === fromTimeBlockActions.timeBlockDeleted.type) {
          const deletedId = response.payload.deletedTimeBlockId;
          this.timeEntriesListViewService.deleteInStore(deletedId);
        } else {
          const deletedIds = response.payload.deletedTimeBlockIds;
          deletedIds.forEach((deletedId) =>
            this.timeEntriesListViewService.deleteInStore(deletedId),
          );
          this.selectedKeys = [];
        }
        this.shiveNotificationService.show(NotificationType.Success);
      });

    // Start / Stop clock
    this.subs.sink = this.actions$
      .pipe(
        ofType(fromTimerActions.timerPosted),
        map((action) => action.payload),
        filter((payload) => payload.generatedTimeBlock !== null),
      )
      .subscribe((payload) => {
        const timerStatus = payload.status;
        const activeTimeBlock = payload.generatedTimeBlock;

        if (timerStatus === TimerStatus.Start) {
          this.sharedClockHandlerService.startClock(activeTimeBlock.timeBlockModel.start.getTime());
        }
      });
  }

  private initContextMenuCallbacks(): void {
    this.gridWrapperComponent.contextMenuItems = [
      {
        text: 'Bearbeiten',
        faIcon: faPen,
        callback: (timeEntry: TimeBlockListEntry) => {
          this.timeEntriesListViewService.openDialog(timeEntry);
        },
        isVisible: true,
        isActivated: true,
      },
      {
        text: 'Löschen',
        faIcon: faTrashArrowUp,
        callback: (timeEntry: TimeBlockListEntry) => {
          this.timeBlockHttpService.deleteBlock(timeEntry.id);
        },
        isVisible: true,
        isActivated: (timeEntry: TimeBlockListEntry) => !timeEntry.isActive,
        iconColorClass: 'red',
      },
    ];
  }

  private initConfigData(): void {
    this.calendarService
      .initConfigData()
      .pipe(take(1))
      .subscribe(() => {
        // Check end time for both timer and tracker entries.
        this.timeEntriesListViewService.updateActiveTimeBlockEndDate();

        this.initHttpCallbacks();
      });
  }
}
